import captureWorkletUrl from './gemini-capture-worklet.js?url&no-inline';

/** Streaming area resampler. Integer time units keep 44.1/48 kHz boundaries exact. */
export class PcmCapture {
  private weight = 0;
  private sum = 0;
  private frame = new Uint8Array(640);
  private offset = 0;
  constructor(private readonly sampleRate: number, private readonly emit: (pcm: Uint8Array) => void) {
    if (!Number.isInteger(sampleRate) || sampleRate < 16_000) throw new Error('Unsupported capture sample rate');
  }
  push(samples: Float32Array): void {
    for (const raw of samples) {
      const sample = Number.isFinite(raw) ? Math.max(-1, Math.min(1, raw)) : 0;
      let remaining = 16_000;
      while (remaining > 0) {
        const used = Math.min(remaining, this.sampleRate - this.weight);
        this.sum += sample * used;
        this.weight += used;
        remaining -= used;
        if (this.weight === this.sampleRate) {
          const value = this.sum / this.sampleRate;
          const pcm = Math.round(value * (value < 0 ? 32768 : 32767));
          this.frame[this.offset++] = pcm & 255;
          this.frame[this.offset++] = (pcm >> 8) & 255;
          this.weight = 0;
          this.sum = 0;
          if (this.offset === this.frame.length) {
            const frame = this.frame;
            this.frame = new Uint8Array(640);
            this.offset = 0;
            this.emit(frame);
          }
        }
      }
    }
  }
  reset(): void { this.weight = 0; this.sum = 0; this.offset = 0; this.frame = new Uint8Array(640); }
}

export class PlaybackQueueExceededError extends Error {
  constructor() { super('Playback queue exceeded'); this.name = 'PlaybackQueueExceededError'; }
}

export class PcmPlayback {
  private readonly sources = new Set<AudioBufferSourceNode>();
  private retainedSeconds = 0;
  private nextStart = 0;
  private generation = 0;
  private lastSequence = -1;
  constructor(private readonly context: AudioContext, private readonly started: (sequence: number) => void, private readonly stopped: (sequence: number) => void) {}
  enqueue(pcm: Uint8Array, sequence: number): void {
    const duration = pcm.length / 48_000;
    const when = Math.max(this.context.currentTime, this.nextStart);
    // Bound pending speech by the audio clock, independently of delayed ended callbacks.
    // Retained samples and source nodes also stay bounded while those callbacks lag.
    if (when - this.context.currentTime + duration > 60.00000001
      || this.retainedSeconds + duration > 61.00000001 || this.sources.size >= 4096) throw new PlaybackQueueExceededError();
    const samples = new Float32Array(pcm.length / 2);
    const view = new DataView(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    for (let i = 0; i < samples.length; i++) samples[i] = view.getInt16(i * 2, true) / 32768;
    const buffer = this.context.createBuffer(1, samples.length, 24_000);
    buffer.copyToChannel(samples, 0);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    const wasIdle = this.sources.size === 0;
    const generation = this.generation;
    this.sources.add(source);
    this.retainedSeconds += duration;
    this.lastSequence = sequence;
    source.onended = () => {
      if (generation !== this.generation || !this.sources.delete(source)) return;
      source.disconnect(); source.onended = null;
      this.retainedSeconds = Math.max(0, this.retainedSeconds - duration);
      if (this.sources.size === 0) this.stopped(this.lastSequence);
    };
    source.start(when);
    this.nextStart = when + duration;
    if (wasIdle) this.started(sequence);
  }
  clear(): void {
    this.generation++;
    for (const source of this.sources) {
      source.onended = null;
      try { source.stop(); } catch { /* A failed start can leave an unstarted source. */ }
      source.disconnect();
    }
    this.sources.clear(); this.retainedSeconds = 0; this.nextStart = 0; this.lastSequence = -1;
  }
}

export interface GeminiAudioLike {
  start(stream: MediaStream, onPcm: (pcm: Uint8Array) => void): Promise<void>;
  setCapturing(enabled: boolean): void;
  enqueue(pcm: Uint8Array, sequence: number): void;
  clearPlayback(): void;
  stopCapture(): void;
  dispose(): void;
}

export class GeminiBrowserAudio implements GeminiAudioLike {
  private readonly context = new AudioContext();
  private readonly playback: PcmPlayback;
  private capture?: PcmCapture;
  private source?: MediaStreamAudioSourceNode;
  private worklet?: AudioWorkletNode;
  private disposed = false;
  private capturing = false;
  private captureEpoch = 0;
  // Constructed synchronously in connect(), within the Start user gesture.
  private readonly resumed = this.context.resume();
  constructor(started: (sequence: number) => void, stopped: (sequence: number) => void) {
    this.playback = new PcmPlayback(this.context, started, stopped);
    void this.resumed.catch(() => {});
  }
  async start(stream: MediaStream, onPcm: (pcm: Uint8Array) => void): Promise<void> {
    await this.resumed;
    if (this.disposed) return;
    await this.context.audioWorklet.addModule(captureWorkletUrl);
    if (this.disposed) return;
    this.capture = new PcmCapture(this.context.sampleRate, onPcm);
    this.source = this.context.createMediaStreamSource(stream);
    this.worklet = new AudioWorkletNode(this.context, 'merlin-pcm-capture');
    this.worklet.port.onmessage = (event: MessageEvent<{ epoch: number; samples: Float32Array }>) => {
      if (this.capturing && event.data.epoch === this.captureEpoch && event.data.samples instanceof Float32Array) this.capture?.push(event.data.samples);
    };
    this.source.connect(this.worklet);
    this.worklet.connect(this.context.destination);
  }
  setCapturing(enabled: boolean): void {
    this.capturing = enabled; this.captureEpoch++; this.capture?.reset();
    this.worklet?.port.postMessage({ enabled, epoch: this.captureEpoch });
  }
  enqueue(pcm: Uint8Array, sequence: number): void { this.playback.enqueue(pcm, sequence); }
  clearPlayback(): void { this.playback.clear(); }
  stopCapture(): void {
    this.setCapturing(false); this.source?.disconnect(); this.source = undefined;
    if (this.worklet) { this.worklet.port.onmessage = null; this.worklet.port.close(); this.worklet.disconnect(); this.worklet = undefined; }
    this.capture = undefined;
  }
  dispose(): void { if (this.disposed) return; this.disposed = true; this.stopCapture(); this.playback.clear(); void this.context.close().catch(() => {}); }
}

export function encodePcm(pcm: Uint8Array): string {
  let binary = '';
  for (const byte of pcm) binary += String.fromCharCode(byte);
  return btoa(binary);
}
export function decodePcm(data: string): Uint8Array { return Uint8Array.from(atob(data), (char) => char.charCodeAt(0)); }
